import 'swiper/css';
import 'swiper/css/scrollbar';
import '../styles/teaser-list.scss';

import Swiper from 'swiper';
import { Keyboard, Scrollbar } from 'swiper/modules';

import { initTeaserListItemPixelate } from './modules/teaser-list-item-pixelate.js';

const initScrollEffect = (sliders) => {
    const update = () => {
        const viewportHeight = window.innerHeight;

        sliders.forEach((slider) => {
            const isNoSlider = slider.closest('.f-teaser-list')?.classList.contains('f-teaser-list--no-slider');

            if (isNoSlider) {
                const items = slider.querySelectorAll('.f-teaser-list-item');
                items.forEach((item) => {
                    const rect = item.getBoundingClientRect();
                    const enterPoint = viewportHeight;
                    const fullPoint = Math.max(0, (viewportHeight - rect.height) * 0.5);

                    let factor = 0;
                    if (rect.top <= fullPoint) {
                        factor = 1;
                    } else if (rect.top < enterPoint && enterPoint > fullPoint) {
                        factor = (enterPoint - rect.top) / (enterPoint - fullPoint);
                    }

                    const clamped = Math.max(0, Math.min(1, factor));
                    item.style.setProperty('--scroll-progress', clamped.toFixed(3));
                });

                return;
            }

            const rect = slider.getBoundingClientRect();
            const enterPoint = viewportHeight;
            // Reaches full progression when the slider is centered vertically in the viewport
            const fullPoint = Math.max(0, (viewportHeight - rect.height) * 0.5);

            let factor = 0;
            if (rect.top <= fullPoint) {
                factor = 1;
            } else if (rect.top < enterPoint && enterPoint > fullPoint) {
                factor = (enterPoint - rect.top) / (enterPoint - fullPoint);
            }

            const clamped = Math.max(0, Math.min(1, factor));
            slider.style.setProperty('--scroll-progress', clamped.toFixed(3));
        });
    };

    let ticking = false;
    const onScrollOrResize = () => {
        if (!ticking) {
            requestAnimationFrame(() => {
                update();
                ticking = false;
            });
            ticking = true;
        }
    };

    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize, { passive: true });
    update();
};

const initTeaserListSlider = () => {
    const sliders = document.querySelectorAll('.f-teaser-list__items');

    if (sliders.length === 0) {
        return;
    }

    sliders.forEach((slider) => {
        if (slider.dataset.swiperInitialized === 'true') {
            return;
        }

        if (!slider.classList.contains('swiper')) {
            return;
        }

        const wrapper = slider.querySelector('.swiper-wrapper');
        if (!wrapper) {
            return;
        }

        Array.from(wrapper.children).forEach((child) => {
            if (!child.classList.contains('swiper-slide') && !child.classList.contains('f-teaser-list__empty')) {
                child.classList.add('swiper-slide');
            }
        });

        slider.dataset.swiperInitialized = 'true';

        const scrollbarEl = slider.parentElement?.querySelector('.f-teaser-list__scrollbar') || slider.querySelector('.swiper-scrollbar');
        const prevButton = slider.parentElement?.querySelector('[data-swiper-nav="prev"]');
        const nextButton = slider.parentElement?.querySelector('[data-swiper-nav="next"]');

        const swiper = new Swiper(slider, {
            modules: [Keyboard, Scrollbar],
            slidesPerView: 3.15,
            spaceBetween: 24,
            grabCursor: true,
            watchOverflow: true,
            centerInsufficientSlides: false,
            speed: 500,
            scrollbar: {
                el: scrollbarEl,
                draggable: true,
                dragSize: 80,
                hide: false,
            },
            keyboard: {
                enabled: true,
            },
            breakpoints: {
                0: {
                    slidesPerView: 1.15,
                    spaceBetween: 16,
                },
                640: {
                    slidesPerView: 2.15,
                    spaceBetween: 20,
                },
                1024: {
                    slidesPerView: 3.15,
                    spaceBetween: 24,
                },
                1440: {
                    slidesPerView: 3.15,
                    spaceBetween: 24,
                },
            },
        });

        const controls = slider.parentElement?.querySelector('.f-teaser-list__controls');

        const updateNavButtonsState = () => {
            if (!prevButton || !nextButton) {
                return;
            }

            const prevDisabled = swiper.isBeginning;
            const nextDisabled = swiper.isEnd;
            const shouldHideControls = prevDisabled && nextDisabled;

            if (controls) {
                controls.hidden = shouldHideControls;
            }

            prevButton.disabled = prevDisabled;
            nextButton.disabled = nextDisabled;
            prevButton.setAttribute('aria-disabled', String(prevDisabled));
            nextButton.setAttribute('aria-disabled', String(nextDisabled));
        };

        swiper.on('slideChange', updateNavButtonsState);
        swiper.on('resize', updateNavButtonsState);
        swiper.on('update', updateNavButtonsState);

        prevButton?.addEventListener('click', () => {
            if (!prevButton.disabled) {
                swiper.slidePrev();
            }
        });
        nextButton?.addEventListener('click', () => {
            if (!nextButton.disabled) {
                swiper.slideNext();
            }
        });

        updateNavButtonsState();
    });

    initScrollEffect(sliders);
};

const initStandaloneScrollEffect = () => {
    const items = document.querySelectorAll('.f-teaser-list-item--standalone');

    if (items.length === 0) {
        return;
    }

    const update = () => {
        const viewportHeight = window.innerHeight;

        items.forEach((item) => {
            const rect = item.getBoundingClientRect();
            const enterPoint = viewportHeight;
            // Fully settled when the item's top has scrolled up into the upper-middle of the viewport (~35% from top)
            const fullPoint = viewportHeight * 0.35;

            let factor = 0;
            if (rect.top <= fullPoint) {
                factor = 1;
            } else if (rect.top < enterPoint && enterPoint > fullPoint) {
                factor = (enterPoint - rect.top) / (enterPoint - fullPoint);
            }

            const clamped = Math.max(0, Math.min(1, factor));
            item.style.setProperty('--scroll-progress', clamped.toFixed(3));
        });
    };

    let ticking = false;
    const onScrollOrResize = () => {
        if (!ticking) {
            requestAnimationFrame(() => {
                update();
                ticking = false;
            });
            ticking = true;
        }
    };

    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize, { passive: true });
    update();
};

const init = () => {
    initTeaserListSlider();
    initStandaloneScrollEffect();
    initTeaserListItemPixelate();
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
